/**
 * MainDashPage — V2 main dashboard with stat cards, top-10 tables,
 * charts (trend, pie, hourly), and area shift breakdown.
 *
 * Fixed 28-day window. Refreshes every 10 minutes. Area type filter drawer.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';

/* ── DashboardInfoTip — ⓘ tooltip next to page title ── */
const DASH_TOOLTIP = [
    'Dashboard data covers the previous 28 days (4 weeks) and includes',
    'Pendant and Pull Cord device events with a response time under 60 minutes.',
];

const DashboardInfoTip = () => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [tipStyle, setTipStyle] = useState({});

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        const spaceBelow = window.innerHeight - rect.bottom;

        setTipStyle({
            position: 'fixed',
            left: spaceRight < 320 ? undefined : `${rect.left}px`,
            right: spaceRight < 320 ? '16px' : undefined,
            top: spaceBelow < 80 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 80 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            maxWidth: '300px',
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)} style={{ cursor: 'help', marginLeft: '8px', color: 'var(--color-text-muted)' }}>
            <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
            {open && (
                <span className="na-type-popover" style={tipStyle}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>About This Dashboard</span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{DASH_TOOLTIP.join(' ')}</span>
                </span>
            )}
        </span>
    );
};
import {
    getSession, getCampusId, getCampusList, updateSessionCampus,
} from '../../../services/session.js';
import { formatDateIso, getToday } from '../../../libs.jsx';
import {
    fetchDashboardStats, fetchResidentSummary, fetchCaregiverSummary,
    fetchAreaSummary, fetchAreaShiftSummary, fetchReasonData,
    fetchHourChartData, fetchDashboardFilterOptions,
} from './api/mainDashApi.js';

import StatCards from './components/StatCards.jsx';
import TopResidentsTable from './components/TopResidentsTable.jsx';
import TopCaregiversTable from './components/TopCaregiversTable.jsx';
import ResponseTrendChart from './components/ResponseTrendChart.jsx';
import ReasonPieChart from './components/ReasonPieChart.jsx';
import AreaShiftTable from './components/AreaShiftTable.jsx';
import HourlyChart from './components/HourlyChart.jsx';
import ResidentTrendActionsV2 from './components/ResidentTrendActions.jsx';

import './main-dash-page.css';

const MainDashPage = () => {
    // ======================================================================
    // SESSION
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);

    // Fixed 28-day window
    const startDate = useMemo(() => formatDateIso(getToday(-28)), []);
    const endDate = useMemo(() => formatDateIso(getToday()), []);

    // ======================================================================
    // DATA STATE
    // ======================================================================
    const [statData, setStatData] = useState({});
    const [residentData, setResidentData] = useState([]);
    const [caregiverData, setCaregiverData] = useState([]);
    const [areaData, setAreaData] = useState([]);
    const [areaShiftData, setAreaShiftData] = useState([]);
    const [reasonData, setReasonData] = useState({});
    const [hourChartData, setHourChartData] = useState({});
    const [loading, setLoading] = useState(true);

    // ======================================================================
    // FILTER STATE
    // ======================================================================
    const [areaTypeOptions, setAreaTypeOptions] = useState([]);
    const [selectedAreaTypes, setSelectedAreaTypes] = useState([]);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [filterSearch, setFilterSearch] = useState('');

    // ======================================================================
    // TOAST
    // ======================================================================
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // ======================================================================
    // LOAD FILTER OPTIONS
    // ======================================================================
    useEffect(() => {
        if (!campusId) return;
        fetchDashboardFilterOptions(campusId).then((types) => {
            setAreaTypeOptions(Array.isArray(types) ? types : []);
        });
    }, [campusId]);

    // ======================================================================
    // LOAD DASHBOARD DATA
    // ======================================================================
    const areaTypeFilter = useMemo(() => selectedAreaTypes.join(','), [selectedAreaTypes]);

    const loadData = useCallback(async () => {
        if (!campusId) return;
        setLoading(true);
        try {
            const [stats, residents, caregivers, areas, shifts, reasons, hourly] = await Promise.all([
                fetchDashboardStats(campusId, startDate, endDate, areaTypeFilter),
                fetchResidentSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchCaregiverSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchAreaSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchAreaShiftSummary(campusId, startDate, endDate, areaTypeFilter),
                fetchReasonData(campusId, startDate, endDate, areaTypeFilter),
                fetchHourChartData(campusId, startDate, endDate, areaTypeFilter),
            ]);
            // Stats comes as array — take first element
            setStatData(Array.isArray(stats) && stats.length > 0 ? stats[0] : stats || {});
            // Residents, caregivers, areas, shifts come as { records: [...], totals: [...] }
            setResidentData(residents?.records || []);
            setCaregiverData(caregivers?.records || []);
            setAreaData(areas?.records || []);
            setAreaShiftData(shifts?.records || []);
            setReasonData(reasons || {});
            setHourChartData(hourly || {});
        } catch (err) {
            console.error('[MainDashPage] loadData error:', err);
            showToast('error', 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [campusId, startDate, endDate, areaTypeFilter, showToast]);

    useEffect(() => { loadData(); }, [loadData]);

    // Polling: 600s (10 minutes)
    useEffect(() => {
        const timer = setInterval(loadData, 600000);
        return () => clearInterval(timer);
    }, [loadData]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setSelectedAreaTypes([]);
    }, []);

    const handleToggleAreaType = useCallback((areaTypeId) => {
        setSelectedAreaTypes((prev) => {
            const id = String(areaTypeId);
            return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        });
    }, []);

    const handleSelectAllAreaTypes = useCallback(() => {
        setSelectedAreaTypes(areaTypeOptions.map((at) => String(at.area_type_id)));
    }, [areaTypeOptions]);

    const handleClearAreaTypes = useCallback(() => {
        setSelectedAreaTypes([]);
    }, []);

    const handleApplyFilter = useCallback(() => {
        setFilterDrawerOpen(false);
    }, []);

    const filteredAreaTypeOptions = useMemo(() => {
        if (!filterSearch) return areaTypeOptions;
        const term = filterSearch.toLowerCase();
        return areaTypeOptions.filter((at) => (at.area_type_name || '').toLowerCase().includes(term));
    }, [areaTypeOptions, filterSearch]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="md-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`} role="status" aria-live="polite">
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="dp-toast__close" aria-label="Dismiss">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* Page Header */}
                    <header className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <span className="dp-breadcrumb__text">Dashboards</span>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">Main</span>
                                </nav>
                                <div className="dp-campus-row">
                                    <label htmlFor="md-campus-selector" className="dp-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="md-campus-selector" className="dp-campus-select" value={campusId} onChange={handleCampusChange}>
                                            {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                        </select>
                                    ) : (
                                        <span className="dp-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-2)' }}>
                                <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-heading)', margin: 0 }}>Dashboard<DashboardInfoTip /></h1>
                                <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setFilterDrawerOpen(true)} style={{ position: 'relative' }}>
                                    <i className="fe fe-filter" style={{ marginRight: '4px' }} />
                                    Filter
                                    {selectedAreaTypes.length > 0 ? (
                                        <span className="ar-filter-badge">{selectedAreaTypes.length}</span>
                                    ) : null}
                                </button>
                            </div>
                        </div>
                    </header>

                    {/* Content */}
                    <div className="md-content">
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-muted)' }}>
                                <div className="ar-loading__spinner" style={{ margin: '0 auto var(--space-3)' }} />
                                <p>Loading dashboard...</p>
                            </div>
                        ) : null}

                        {!loading ? (
                            <>
                        {/* Stat Cards */}
                        <StatCards statData={statData} loading={loading} />

                        {/* Top 10 Tables (side by side) */}
                        <div className="md-two-col">
                            <TopResidentsTable data={residentData} />
                            <TopCaregiversTable data={caregiverData} />
                        </div>

                        {/* Charts (side by side) */}
                        <div className="md-two-col">
                            <ResponseTrendChart areaData={areaData} />
                            <ReasonPieChart reasonData={reasonData} />
                        </div>

                        {/* Area Shift Table (full width) */}
                        <AreaShiftTable data={areaShiftData} />

                        {/* Hourly Chart (full width) */}
                        <HourlyChart chartData={hourChartData} avgResponseTime={statData?.avg_response_time} />

                        {/* Resident Trends & Actions (conditional on campus feature flag) */}
                        {campusConfig?.resident_trends ? (
                            <ResidentTrendActionsV2 campusId={campusId} />
                        ) : null}
                            </>
                        ) : null}
                    </div>

                    {/* Filter Drawer (area types only) */}
                    {filterDrawerOpen ? <div className="dp-drawer-backdrop" onClick={() => setFilterDrawerOpen(false)} /> : null}
                    <div className={`dp-drawer dp-drawer--lg${filterDrawerOpen ? ' is-open' : ''}`}>
                        <div className="dp-drawer__header">
                            <h2 className="dp-drawer__title">Filter by Area Type</h2>
                            <button className="dp-drawer__close" onClick={() => setFilterDrawerOpen(false)}>{'\u2715'}</button>
                        </div>
                        <div className="dp-drawer__body" style={{ overflowY: 'auto', flex: 1 }}>
                            <input
                                type="text"
                                className="md-filter-search"
                                placeholder="Search area types..."
                                value={filterSearch}
                                onChange={(e) => setFilterSearch(e.target.value)}
                            />
                            <div className="md-filter-actions">
                                <button className="rp-btn rp-btn--secondary" onClick={handleSelectAllAreaTypes} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>Select All</button>
                                <button className="rp-btn rp-btn--secondary" onClick={handleClearAreaTypes} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>Clear</button>
                            </div>
                            <div className="md-filter-list">
                                {filteredAreaTypeOptions.map((at) => {
                                    const id = String(at.area_type_id);
                                    return (
                                        <label key={id} className="md-filter-item">
                                            <input type="checkbox" checked={selectedAreaTypes.includes(id)} onChange={() => handleToggleAreaType(id)} />
                                            <span>{at.area_type_name || at.area_type_description || '(unknown)'}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="dp-drawer__footer">
                            <button className="dp-btn dp-btn--cancel" onClick={handleClearAreaTypes}>Clear All</button>
                            <button className="dp-btn dp-btn--primary" onClick={handleApplyFilter}>Apply Filter</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MainDashPage;
